import React, { useState } from 'react';
import { PlusCircle, Trash2, GripVertical } from 'lucide-react';

interface GroupingDimension {
  field: string;
  label: string;
}

interface GroupingControlsProps {
  groupingModel: string[];
  onGroupingModelChange: (model: string[]) => void;
  availableDimensions: GroupingDimension[];
  /** Called with the chosen name when the user confirms the inline save form. */
  onSavePreset: (name: string) => void;
  /** Names of existing user presets — used to detect duplicates in the save form. */
  existingPresetNames?: string[];
}

/**
 * GroupingControls - Reusable component for configuring row grouping
 *
 * Features:
 * - Drag-and-drop reordering of grouping levels
 * - Add/remove grouping levels
 * - Visual hierarchy display
 * - Collapsible panel to save vertical space
 */
export const GroupingControls: React.FC<GroupingControlsProps> = ({
  groupingModel,
  onGroupingModelChange,
  availableDimensions,
  onSavePreset,
  existingPresetNames = [],
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  // Inline save-as-preset form state
  const [isSaveFormOpen, setIsSaveFormOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState('');

  // Get all dimensions (current + available)
  const allDimensions = [
    ...groupingModel.map(field => {
      const dim = availableDimensions.find(d => d.field === field);
      return dim || { field, label: field };
    }),
    ...availableDimensions.filter(d => !groupingModel.includes(d.field)),
  ];

  // Get available dimensions for a specific level (exclude others already in use)
  const getAvailableForLevel = (currentField: string): GroupingDimension[] => {
    return allDimensions.filter(
      d => d.field === currentField || !groupingModel.includes(d.field)
    );
  };

  // Get dimensions not yet in use (for Add Level dropdown)
  const getUnusedDimensions = (): GroupingDimension[] => {
    return allDimensions.filter(d => !groupingModel.includes(d.field));
  };

  // Handlers
  const handleAddLevel = (field: string) => {
    if (field) {
      onGroupingModelChange([...groupingModel, field]);
    }
  };

  const handleRemoveLevel = (index: number) => {
    const newModel = groupingModel.filter((_, i) => i !== index);
    onGroupingModelChange(newModel);
  };

  const handleChangeLevel = (index: number, newField: string) => {
    const newModel = [...groupingModel];
    newModel[index] = newField;
    onGroupingModelChange(newModel);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };
  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newModel = [...groupingModel];
    const [draggedItem] = newModel.splice(draggedIndex, 1);
    newModel.splice(dropIndex, 0, draggedItem);

    onGroupingModelChange(newModel);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleConfirmSave = () => {
    const trimmed = saveName.trim();
    if (!trimmed) {
      setSaveError('Name is required.');
      return;
    }
    if (existingPresetNames.includes(trimmed)) {
      setSaveError(`"${trimmed}" already exists.`);
      return;
    }
    onSavePreset(trimmed);
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
  };

  const handleCancelSave = () => {
    setIsSaveFormOpen(false);
    setSaveName('');
    setSaveError('');
  };

  const unusedDimensions = getUnusedDimensions();

  return (
    <div>
      {/* Current Grouping Levels */}
      <div style={styles.levelsContainer}>
        {groupingModel.map((field, index) => {
          const availableForThisLevel = getAvailableForLevel(field);
          const isDragging = draggedIndex === index;
          const isDragOver = dragOverIndex === index;

          return (
            <div
              key={index}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={e => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              style={{
                ...styles.levelRow,
                ...(isDragging ? styles.levelRowDragging : {}),
                ...(isDragOver ? styles.levelRowDragOver : {}),
              }}>
              <div title="Drag to reorder">
                <GripVertical size={16} style={styles.dragHandle} />
              </div>
              <span style={styles.levelChip}>Level {index + 1}</span>
              <select
                value={field}
                onChange={e => handleChangeLevel(index, e.target.value)}
                style={styles.select}>
                {availableForThisLevel.map(dim => (
                  <option key={dim.field} value={dim.field}>
                    {dim.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => handleRemoveLevel(index)}
                disabled={groupingModel.length === 1}
                style={
                  groupingModel.length === 1
                    ? styles.iconButtonDisabled
                    : styles.iconButton
                }
                title="Remove this level">
                <Trash2 size={16} />
              </button>
              {index === 0 && (
                <span style={styles.primaryLabel}>(Primary grouping)</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Level Control */}
      {unusedDimensions.length > 0 && (
        <div style={styles.addLevelRow}>
          <select
            value=""
            onChange={e => handleAddLevel(e.target.value)}
            style={styles.select}>
            <option value="" disabled>
              Add Grouping Level
            </option>
            {unusedDimensions.map(dim => (
              <option key={dim.field} value={dim.field}>
                {dim.label}
              </option>
            ))}
          </select>
          <button style={styles.iconButtonDisabled} disabled>
            <PlusCircle size={16} />
          </button>
        </div>
      )}

      {/* Save as Preset */}
      <div style={styles.saveRow}>
        {isSaveFormOpen ? (
          <div style={styles.saveForm}>
            <input
              type="text"
              placeholder="Preset name…"
              value={saveName}
              onChange={e => {
                setSaveName(e.target.value);
                setSaveError('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handleConfirmSave();
                if (e.key === 'Escape') handleCancelSave();
              }}
              autoFocus
              style={styles.saveNameInput}
              data-testid="grouping-preset-name-input"
            />
            {saveError && <span style={styles.saveErrorText}>{saveError}</span>}
            <button
              onClick={handleConfirmSave}
              style={styles.saveButton}
              data-testid="grouping-preset-save-confirm">
              Save
            </button>
            <button onClick={handleCancelSave} style={styles.iconButton}>
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setSaveName('');
              setSaveError('');
              setIsSaveFormOpen(true);
            }}
            style={styles.saveButton}
            data-testid="grouping-preset-save-button">
            💾 Save as Preset
          </button>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  levelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    paddingBottom: '0.75rem',
  },
  levelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    cursor: 'grab',
    padding: '0.25rem',
    borderRadius: '0.25rem',
    transition: 'all 0.15s',
  },
  levelRowDragging: {
    opacity: 0.5,
    cursor: 'grabbing',
  },
  levelRowDragOver: {
    backgroundColor: '#e0e7ff',
    borderTop: '2px solid #1976d2',
  },
  dragHandle: {
    color: '#9ca3af',
    cursor: 'grab',
    flexShrink: 0,
  },
  levelChip: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#1976d2',
    padding: '0.25rem 0.5rem',
    border: '1px solid #1976d2',
    borderRadius: '0.25rem',
    minWidth: '60px',
    textAlign: 'center',
    flexShrink: 0,
  },
  select: {
    minWidth: '200px',
    padding: '0.5rem',
    fontSize: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    cursor: 'pointer',
  },
  iconButton: {
    padding: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    flexShrink: 0,
  },
  iconButtonDisabled: {
    padding: '0.5rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#d1d5db',
    flexShrink: 0,
  },
  primaryLabel: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  addLevelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    paddingBottom: '0.75rem',
  },
  saveRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: '0.25rem',
    borderTop: '1px solid #e5e7eb',
    marginTop: '0.5rem',
  },
  saveForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
    width: '100%',
  },
  saveNameInput: {
    flex: '1 1 160px',
    padding: '0.375rem 0.5rem',
    fontSize: '0.8125rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.25rem',
    outline: 'none',
  },
  saveErrorText: {
    fontSize: '0.75rem',
    color: '#dc2626',
    flex: '1 1 100%',
  },
  saveButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    border: '1px solid #60a5fa',
    borderRadius: '0.25rem',
    backgroundColor: 'white',
    color: '#1e40af',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
};
